import dayjs, { Dayjs } from "dayjs";
import { logException, logMessage } from "@/analytics/sentry";

import { ComedianLite } from "./comedians";
import { Database } from "@/data/supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import { VenueRow } from "./venues";
import createNoAuthSupabaseClient from "@/data/supabase";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { getFollowedComedians } from "./follows";

dayjs.extend(customParseFormat);

export type TicketFlags = Database["public"]["Enums"]["ticket_flags"];

export interface ShowInfo {
  id: string;
  comedian_id: string;
  datetime: Dayjs;
  datetimeOriginal: string | null;
  location: string;
  venue: string;
  ticketLink: string;
  venueId: string;
  soldOut: boolean;
  metadataText: string;
  vipTicketLink: string;
  presaleCode: string;
  flags: TicketFlags[];
  title?: string;
  tixologiEventId: string | null;
}

export type ShowInfoWithVenue = Omit<
  ShowInfo,
  "datetime" | "datetimeOriginal"
> & {
  datetime: string;
  venue: Partial<VenueRow>;
};

export type ShowRowWithVenue = ShowInfoRow & {
  venue: Partial<VenueRow>;
};

// Need to do this because ShowInfo contains a date of type Dayjs, which Next.js complains about occasionally
// with the error: "Warning: Only plain objects can be passed to Client Components from Server Components.
// Objects with toJSON methods are not supported. Convert it manually to a simple value before passing it to props."
export interface ShowInfoDateStringified extends Omit<ShowInfo, "datetime"> {
  datetime: string | undefined;
}

export type ShowInfoRow = Database["public"]["Tables"]["comedian_shows"]["Row"];
export type ShowInfoInsert =
  Database["public"]["Tables"]["comedian_shows"]["Insert"];

export function processShowData(data: ShowInfoRow): ShowInfo {
  return {
    id: data.id,
    comedian_id: data.comedian_id,
    datetime: dayjs(data.datetime),
    datetimeOriginal: data.datetime,
    location: data.location,
    venue: data.venue,
    ticketLink: data.ticket_link,
    venueId: data.venue_id || "",
    soldOut: data.is_sold_out || false,
    metadataText: data.metadata_text || "",
    vipTicketLink: data.vip_ticket_link || "",
    presaleCode: data.presale_code || "",
    flags: (data.flags || []) as TicketFlags[],
    title: data.title || "",
    tixologiEventId: data.tixologi_event_id || null,
  };
}

export function processShowDataWithVenue(
  data: ShowInfoRow & { venue: Partial<VenueRow> }
): ShowInfoWithVenue {
  return {
    id: data.id,
    comedian_id: data.comedian_id,
    datetime: data.datetime || "",
    location: data.location,
    ticketLink: data.ticket_link,
    venueId: data.venue_id || "",
    soldOut: data.is_sold_out || false,
    metadataText: data.metadata_text || "",
    vipTicketLink: data.vip_ticket_link || "",
    venue: data.venue,
    presaleCode: data.presale_code || "",
    flags: (data.flags || []) as TicketFlags[],
    title: data.title || "",
    tixologiEventId: data.tixologi_event_id || null,
  };
}

// Returns all future shows for a given comedian. Automatically takes care
// of pagination (keeping in mind Supabase's limit of 1000 records per query)
// in order to provide an easy to use API (just call one function and get all shows)
// and avoiding to have to handle pagination in the front-end components.
export async function getAllFutureShowsForComedian(
  comedianID: string,
  supabase?: SupabaseClient<Database>
): Promise<ShowInfo[]> {
  const allFutureShows = [];
  // Defines how many shows to fetch per query, 1000 is the maximum value for Supabase
  const PAGE_SIZE = 1000;
  let cursor = 0;
  let done = false;
  while (!done) {
    const venues = await getShowsRangeForComedian(
      comedianID,
      cursor,
      cursor + PAGE_SIZE - 1,
      false,
      supabase
    );
    allFutureShows.push(...venues);
    cursor += PAGE_SIZE;
    if (venues.length < PAGE_SIZE) {
      done = true;
    }
  }
  return allFutureShows as ShowInfo[];
}

// Same but with venue
export async function getAllFutureShowsWithVenuesForComedian(
  comedianID: string,
  supabase?: SupabaseClient<Database>
): Promise<ShowRowWithVenue[]> {
  const allFutureShows = [];
  // Defines how many shows to fetch per query, 1000 is the maximum value for Supabase
  const PAGE_SIZE = 1000;
  let cursor = 0;
  let done = false;
  while (!done) {
    const venues = await getShowsRangeForComedian(
      comedianID,
      cursor,
      cursor + PAGE_SIZE - 1,
      true,
      supabase
    );
    allFutureShows.push(...venues);
    cursor += PAGE_SIZE;
    if (venues.length < PAGE_SIZE) {
      done = true;
    }
  }
  return allFutureShows as ShowRowWithVenue[];
}

async function getShowsRangeForComedian(
  comedianID: string,
  start: number,
  end: number,
  withVenues: boolean = false,
  supabaseClient?: SupabaseClient<Database>
): Promise<(ShowInfo | ShowRowWithVenue)[]> {
  const supabase = supabaseClient || createNoAuthSupabaseClient();
  const today = dayjs().format("YYYY-MM-DD");

  if (withVenues) {
    const { data } = await supabase
      .from("comedian_shows")
      .select(
        "*, venue:comedian_shows_venue_id_fkey!inner (id, name, address, city, state, country, postal_code)"
      )
      .eq("comedian_id", comedianID)
      .gte("datetime", today)
      .order("datetime")
      .range(start, end)
      .throwOnError();
    if (data) {
      return data;
    }
  } else {
    const { data } = await supabase
      .from("comedian_shows")
      .select("*")
      .eq("comedian_id", comedianID)
      .gte("datetime", today)
      .order("datetime")
      .range(start, end)
      .throwOnError();
    if (data) {
      return data.map((row) => processShowData(row));
    }
  }

  throw new Error(`Issue finding shows for ${comedianID}.`);
}

export type ShowsAndComediansPerforming = {
  comediansPerforming: {
    id: string;
    slug: string;
    display_name: string | null;
    about_img: string | null;
  }[];
  shows: (ShowInfo & { created_at: string | null })[];
};

export async function getShowsAndComediansCreatedAfterCertainDate(
  startDate: string
): Promise<ShowsAndComediansPerforming> {
  const supabase = createNoAuthSupabaseClient();
  const today = dayjs().format("YYYY-MM-DD");
  const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
  const { data, error } = await supabase
    .from("comedian_shows")
    .select(
      `
    *,
    comedian:comedians!comedian_shows_comedian_id_fkey!inner (
      id,
      slug,
      display_name,
      about_img
    )
    `
    )
    .gte("datetime", today)
    .gt("created_at", formattedStartDate)
    .order("comedian_id")
    .range(0, 999); // TODO remember to refactor this in order to support pagination

  if (error) {
    console.error(error);
  } else if (data) {
    const shows: (ShowInfo & { created_at: string | null })[] = [];
    const comediansPerforming: {
      id: string;
      slug: string;
      display_name: string | null;
      about_img: string | null;
    }[] = [];
    const alreadyAdded: string[] = [];

    data.forEach((row) => {
      if (row.comedian) {
        const comedianObj = {
          id: row.comedian?.id,
          slug: row.comedian?.slug,
          display_name: row.comedian?.display_name,
          about_img: row.comedian?.about_img,
        };
        if (!alreadyAdded.includes(row.comedian?.id)) {
          alreadyAdded.push(row.comedian?.id);
          comediansPerforming.push(comedianObj);
        }
      }
      shows.push({ ...processShowData(row), created_at: row.created_at });
    });
    return { comediansPerforming, shows };
  }
  throw new Error(`Issue finding shows for created after a certain date.`);
}

export async function getShow(showId: string): Promise<ShowInfo | null> {
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase
    .from("comedian_shows")
    .select("*")
    .eq("id", showId)
    .limit(1)
    .single();
  if (error) {
    console.error(error);
    throw new Error(`Show with id: ${showId} not found in shows table.`);
  } else if (data) {
    return processShowData(data);
  }
  return null;
}

export type ShowComedianPairing = {
  show_id: string;
  comedian_id: string;
  ordering: number;
  hidden_from_comedian_page: boolean;
};

export async function createOrUpdateShows({
  supabase,
  shows,
  showComedianPairings,
  callerIsServer,
}: {
  supabase: SupabaseClient<Database>;
  shows: Omit<ShowInfoRow, "created_at">[];
  showComedianPairings: ShowComedianPairing[];
  callerIsServer: boolean;
}) {
  if (!callerIsServer) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) {
      throw new Error(
        "Supabase client must have an active session to create/update a show."
      );
    }
  }

  const { error } = await supabase.rpc("create_update_shows_with_comedians", {
    show_records: shows,
    show_comedian_pairings: showComedianPairings,
  });

  if (error) {
    console.error(error);
    logException(error, {
      data: `Error creating/updating shows with multiple comedians. Shows: ${JSON.stringify(shows)}`,
    });
    return { error: error };
  }

  return { error: null };
}

export async function deleteOneShow(
  supabase: SupabaseClient<Database>,
  show_id: string
) {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.user) {
    throw new Error(
      "Supabase client must have an active session to delete shows."
    );
  }

  const { status, error } = await supabase
    .from("comedian_shows")
    .delete()
    .eq("id", show_id);

  if (error) {
    logException(error, {
      data: `Error deleting show with ID: ${show_id}`,
    });
    console.error(error);
    return { error: error };
  } else if (status === 204) {
    return { error: null };
  }
  logException(
    `Generic error while attempting to delete show with ID: ${show_id}`
  );
  throw new Error(`Error deleting show with id ${show_id}`);
}

export type ShowWithComedian = ShowInfoRow & {
  comedian: ComedianLite & { is_punchup_verified: boolean };
  show_comedians: (ComedianLite & {
    is_punchup_verified: boolean;
    ordering: number;
    hidden_from_comedian_page: boolean;
  })[];
};

/**
 * Get shows for a given location
 *
 * @param [Optional] startDate
 * @param [Optional] followedComedians
 *  - true: only shows by followed comedians
 *  - false: only shows by comedians not followed
 *  - undefined: all shows
 * @returns
 */
export async function getShowsByLocationId(
  locationId: string,
  startDate?: string,
  followedComedians?: boolean
): Promise<ShowWithComedian[]> {
  const supabaseClient = createNoAuthSupabaseClient();

  const query = supabaseClient
    .from("comedian_shows")
    .select(
      `
      *,
      comedian:comedians!comedian_shows_comedian_id_fkey(
        id,
        slug,
        display_name,
        display_name_short,
        about_img,
        is_punchup_verified
      ),
      show_comedians:show_comedian_pairings(
        ordering,
        hidden_from_comedian_page,
        comedian:comedians!show_comedian_pairings_comedian_id_fkey(
          id,
          slug,
          display_name,
          display_name_short,
          about_img,
          is_punchup_verified
        )
      )
    `
    )
    .eq("venue_id", locationId)
    .order("comedian_id", { ascending: true });

  if (startDate) {
    query.gte("datetime", startDate);
  }
  if (followedComedians != undefined) {
    const comedianIds = (
      await getFollowedComedians({
        supabase: supabaseClient,
        callerIsServer: true,
      })
    ).map((comedian) => comedian.comedian_id);
    if (followedComedians) {
      query.in("comedian_id", comedianIds);
    } else {
      query.not("comedian_id", "in", comedianIds);
    }
  }
  const { data } = await query.throwOnError();
  if (data === null) {
    throw new Error(
      `Unable to retrieve shows for venue ${locationId}. Database returned null instead of an empty array.`
    );
  }

  return data.map((show) => ({
    ...show,
    show_comedians: show.show_comedians
      .map((sc) => ({
        ...sc.comedian,
        ordering: sc.ordering || 0,
        hidden_from_comedian_page: sc.hidden_from_comedian_page || false,
      }))
      .sort((a, b) => a.ordering - b.ordering),
  })) as ShowWithComedian[];
}

/**
 * Get nearby shows for a given location
 * [Client or Server component]
 * @param locations A list of location IDs to fetch shows for (formerly known as venue_ids)
 * @param limitDays [Optional] Limit of how many days in the future to look for shows
 * @param comedianId [Optional] Filter shows by comedian
 * @param followedComedians [Optional] Filter shows by followed comedians
 *  - true: only shows by followed comedians
 *  - false: only shows by comedians not followed
 *  - undefined: all shows
 * @param includeNonPublicShows [Optional] Include shows that are not public, aka shows of non published comedians AND external preview
 *  - true: include non public shows
 *  - false: only include public shows
 *  - undefined: only include public shows
 * @returns
 */
export async function getShows(
  supabase: SupabaseClient<Database>,
  {
    locations,
    limitDays,
    comedianId,
    followedComedians,
    includeNonPublicShows,
    fetchFromDatetime,
  }: {
    locations?: string[];
    limitDays?: number;
    comedianId?: string;
    followedComedians?: boolean;
    includeNonPublicShows?: boolean;
    fetchFromDatetime?: string;
  }
): Promise<ShowWithComedian[]> {
  if (locations && locations.length === 0) {
    return [];
  }

  const fetchFrom =
    fetchFromDatetime && dayjs(fetchFromDatetime).isValid()
      ? dayjs(fetchFromDatetime).toISOString()
      : dayjs().subtract(12, "hours").toISOString();

  let baseQuery = supabase
    .from("comedian_shows")
    .select(
      `*,
      comedian:comedians!comedian_shows_comedian_id_fkey (
        id,
        display_name,
        display_name_short,
        slug,
        published_at,
        is_external_preview,
        about_img,
        is_punchup_verified
      ),
      show_comedians:show_comedian_pairings(
        ordering,
        hidden_from_comedian_page,
        comedian:comedians!show_comedian_pairings_comedian_id_fkey(
          id,
          display_name,
          display_name_short,
          slug,
          published_at,
          is_external_preview,
          about_img,
          is_punchup_verified
        )
      )`
    )
    .gte("datetime", fetchFrom);

  if (locations) {
    baseQuery = baseQuery.in("venue_id", locations);
  }

  if (limitDays) {
    baseQuery = baseQuery.lte(
      "datetime",
      dayjs().add(limitDays, "days").format("YYYY-MM-DD")
    );
  }
  if (comedianId && followedComedians !== undefined) {
    // Help devs catch that this is invalid combination
    throw new Error("Cannot filter by both comedianId and followedComedians");
  }
  if (comedianId) {
    baseQuery = baseQuery.eq("comedian_id", comedianId);
  }
  if (followedComedians !== undefined) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      const comedianIds = (
        await getFollowedComedians({
          supabase: supabase,
          callerIsServer: true,
        })
      ).map((comedian) => comedian.comedian_id);
      if (followedComedians) {
        baseQuery.in("comedian_id", comedianIds);
      } else {
        baseQuery.not("comedian_id", "in", comedianIds);
      }
    } else {
      logMessage("User is not logged in, skipping followedComedians filter", {
        level: "warning",
      });
    }
  }

  const { data } = await baseQuery.order("datetime", { ascending: true });

  const shows = data?.filter((show) => {
    // Case 1: Return all shows regardless of the comedian's publishing status
    // This is used when we want to display shows of non published comedians AND external preview, for instance
    if (includeNonPublicShows) {
      return true;
    }

    // Case 2: return only shows that should be visible by the public
    // i.e. shows where the comedian is published and is not an external preview
    return (
      show.comedian?.id &&
      ((!show.comedian.is_external_preview &&
        dayjs().isAfter(show.comedian.published_at)) || // return shows for published punchup comedians who are not external previews
        !show.comedian.is_punchup_verified) // return any shows by non punchup verified comedians
    );
  });
  if (!shows) {
    return [];
  }

  return shows.map((show) => ({
    ...show,
    comedian: {
      id: show.comedian?.id || "",
      display_name: show.comedian?.display_name || "",
      display_name_short: show.comedian?.display_name_short || "",
      slug: show.comedian?.slug || "",
      about_img: show.comedian?.about_img || null,
      is_punchup_verified: show.comedian?.is_punchup_verified || false,
    },
    show_comedians: show.show_comedians
      .map((sc) => ({
        ...sc.comedian,
        ordering: sc.ordering || 0,
        hidden_from_comedian_page: sc.hidden_from_comedian_page || false,
      }))
      .sort((a, b) => a.ordering - b.ordering),
  })) as ShowWithComedian[];
}
